import React, { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Folder, GripVertical, X } from 'lucide-react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormModal } from '@/components/ui/form-modal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { defaultSectionTemplates } from 'components/DisasterGPT/SitrepDefaults';
import { OPORD_ANALYTICS_SECTION_TEMPLATE_ID } from 'components/DisasterGPT/constants/opordSectionDefaults';
import {
  createSectionTemplate,
  useSectionTemplates,
} from 'components/DisasterGPT/hooks/useSectionTemplates';
import {
  useCreateSitrepTemplate,
  useSitrepTemplates,
} from 'components/DisasterGPT/hooks/useSitrepTemplates';
import { useAppSelector, selectGroupGuid } from 'slices/commonSelectors';
import { useCreateReportTemplatesFlag } from 'utils/featureFlags';
import {
  addCategory,
  addFromLibrary,
  addSection,
  applyOutlineDrag,
  type OutlineDropPlacement,
  buildNestedTemplateSections,
  canHoldChildren,
  canSubmitOutline,
  cloneOutlineFromTemplate,
  cloneTemplateName,
  createEmptyOutline,
  groupSectionLibrary,
  isAnalyticsRow,
  isCategoryRow,
  mergeSectionLibrary,
  removeOutlineRow,
  titledOutlineRowIds,
  updateOutlineTitle,
  usedSectionTemplateIds,
  type DraftTemplateOutlineRow,
  type NestedTemplateSectionLink,
  type SectionLibraryItem,
  type TemplateSectionLinkInput,
} from './utils/createDraftTemplateOutline';

const AMERICA_250_TEMPLATE_ID = '11111111-1111-1111-1111-111111111111';
const AMERICA_250_TEMPLATE_NAME = 'America 250 events';
export const OPORD_TEMPLATE_ID = '22222222-2222-2222-2222-222222222222';
const OPORD_TEMPLATE_NAME = 'OPORD';
const USDA_TEMPLATE_ID = '33333333-3333-3333-3333-333333333333';
const USDA_TEMPLATE_NAME = 'USDA';

const SYSTEM_TEMPLATE_IDS = new Set([
  AMERICA_250_TEMPLATE_ID,
  OPORD_TEMPLATE_ID,
  USDA_TEMPLATE_ID,
]);

type SitrepTemplateOption = {
  id: string;
  name: string;
  sections?: TemplateSectionLinkInput[];
};

const FALLBACK_TEMPLATES: SitrepTemplateOption[] = [
  {
    id: AMERICA_250_TEMPLATE_ID,
    name: AMERICA_250_TEMPLATE_NAME,
    sections: [],
  },
  { id: OPORD_TEMPLATE_ID, name: OPORD_TEMPLATE_NAME, sections: [] },
  { id: USDA_TEMPLATE_ID, name: USDA_TEMPLATE_NAME, sections: [] },
];

export const DEFAULT_DRAFT_TEMPLATE_ID = AMERICA_250_TEMPLATE_ID;

/**
 * Org templates from the API plus the system templates, which are always
 * offered.
 */
export function useDraftReportTemplates() {
  const templatesQuery = useSitrepTemplates();
  const templates = useMemo(() => {
    const fromApi = (templatesQuery.data ?? []) as SitrepTemplateOption[];
    const byId = new Map(fromApi.map((template) => [template.id, template]));
    for (const fallback of FALLBACK_TEMPLATES) {
      if (!byId.has(fallback.id)) byId.set(fallback.id, fallback);
    }
    return [...byId.values()];
  }, [templatesQuery.data]);
  return templates;
}

export type CreateDraftReportSelection = {
  templateId: string;
  skipDefaultSections: boolean;
};

type CreateDraftReportModalProps = {
  show: boolean;
  onHide: () => void;
  onConfirm: (selection: CreateDraftReportSelection) => void;
  isSubmitting: boolean;
  preferredTemplateId?: string;
  title?: string;
  /** Confirm button when picking an existing template. */
  confirmLabel?: string;
  /** Confirm button when saving a cloned or new template. */
  editConfirmLabel?: string;
};

type ModalMode = 'pick' | 'edit';

/** A pointer over a row beats the category box that wraps that row. */
const outlineCollisionDetection: CollisionDetection = (args) => {
  const hits = pointerWithin(args);
  return hits.length > 0 ? hits : closestCenter(args);
};

const CreateDraftReportModal: React.FC<CreateDraftReportModalProps> = ({
  show,
  onHide,
  onConfirm,
  isSubmitting,
  preferredTemplateId,
  title = 'Create Draft Report',
  confirmLabel = 'Create draft',
  editConfirmLabel = 'Save and create draft',
}) => {
  const groupGuid = useAppSelector(selectGroupGuid);
  const canCreateReportTemplates = useCreateReportTemplatesFlag();
  const templates = useDraftReportTemplates();
  const sectionTemplatesQuery = useSectionTemplates();
  const createSitrepTemplate = useCreateSitrepTemplate();

  const [mode, setMode] = useState<ModalMode>('pick');
  const [selectedOption, setSelectedOption] = useState(AMERICA_250_TEMPLATE_ID);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [outline, setOutline] =
    useState<DraftTemplateOutlineRow[]>(createEmptyOutline);
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [libraryQuery, setLibraryQuery] = useState('');
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);

  useEffect(() => {
    if (!show || !preferredTemplateId) return;
    setSelectedOption(preferredTemplateId);
  }, [show, preferredTemplateId]);

  const libraryItems = useMemo(() => {
    const orgItems: SectionLibraryItem[] = (
      sectionTemplatesQuery.data ?? []
    ).map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
    }));
    const defaultItems: SectionLibraryItem[] = defaultSectionTemplates.map(
      (item: { id: string; name: string; type: string }) => ({
        id: item.id,
        name: item.name,
        type: item.type as SectionLibraryItem['type'],
      })
    );
    return mergeSectionLibrary([
      [
        {
          id: OPORD_ANALYTICS_SECTION_TEMPLATE_ID,
          name: 'Analytics',
          type: 'automated',
        },
      ],
      orgItems,
      defaultItems,
    ]);
  }, [sectionTemplatesQuery.data]);

  const nameBySectionId = useMemo(() => {
    const names: Record<string, string> = {};
    for (const item of libraryItems) names[item.id] = item.name;
    names[OPORD_ANALYTICS_SECTION_TEMPLATE_ID] = 'Analytics';
    return names;
  }, [libraryItems]);

  const libraryGroups = useMemo(() => {
    const q = libraryQuery.trim().toLowerCase();
    const filtered = q
      ? libraryItems.filter((item) => item.name.toLowerCase().includes(q))
      : libraryItems;
    return groupSectionLibrary(filtered);
  }, [libraryItems, libraryQuery]);

  const usedCatalogIds = usedSectionTemplateIds(outline);
  const selectedParent =
    outline.find((row) => row.id === selectedParentId && isCategoryRow(row)) ??
    null;
  const canSubmitEdit =
    newTemplateName.trim().length > 0 && canSubmitOutline(outline);

  const resetEditor = () => {
    setMode('pick');
    setNewTemplateName('');
    setOutline(createEmptyOutline());
    setSelectedParentId(null);
    setLibraryQuery('');
  };

  const handleHide = () => {
    if (isSubmitting || isCreatingTemplate) return;
    resetEditor();
    onHide();
  };

  const startClone = (template: SitrepTemplateOption) => {
    setMode('edit');
    setNewTemplateName(cloneTemplateName(template.name));
    setOutline(
      cloneOutlineFromTemplate(template.sections ?? [], nameBySectionId)
    );
    setSelectedParentId(null);
  };

  const startNewTemplate = () => {
    setMode('edit');
    setNewTemplateName('');
    setOutline(createEmptyOutline());
    setSelectedParentId(null);
    setLibraryQuery('');
  };

  const saveEditedTemplate = async () => {
    if (!canSubmitEdit || !groupGuid) {
      toast.error('Enter a template name and at least one section.');
      return;
    }
    setIsCreatingTemplate(true);
    try {
      const titleIdToSectionId: Record<string, string> = {};
      for (const rowId of titledOutlineRowIds(outline)) {
        const row =
          outline.find((item) => item.id === rowId) ??
          outline
            .flatMap((item) => item.children)
            .find((item) => item.id === rowId);
        const section = await createSectionTemplate(
          {
            name: row?.title.trim() ?? '',
            type: 'manual',
            output_format: '<p></p>',
            output_format_structured: { summary_options: [] },
          },
          groupGuid
        );
        titleIdToSectionId[rowId] = section.id;
      }
      const sections = buildNestedTemplateSections(outline, titleIdToSectionId);
      const createTemplate =
        createSitrepTemplate.mutateAsync as unknown as (data: {
          name: string;
          sections: NestedTemplateSectionLink[];
        }) => Promise<{ id: string }>;
      const template = await createTemplate({
        name: newTemplateName.trim(),
        sections,
      });
      onConfirm({
        templateId: template.id,
        skipDefaultSections: false,
      });
      resetEditor();
    } catch {
      toast.error('Failed to create template.');
    } finally {
      setIsCreatingTemplate(false);
    }
  };

  const handleConfirm = async () => {
    if (mode === 'edit') {
      await saveEditedTemplate();
      return;
    }
    onConfirm({
      templateId: selectedOption,
      skipDefaultSections: false,
    });
  };

  const busy = isSubmitting || isCreatingTemplate;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id;
    if (!overId) return;
    const translated = event.active.rect.current.translated;
    const overRect = event.over?.rect;
    let placement: OutlineDropPlacement = 'after';
    if (translated && overRect) {
      const activeCenter = translated.top + translated.height / 2;
      const overCenter = overRect.top + overRect.height / 2;
      placement = activeCenter < overCenter ? 'before' : 'after';
    }
    setOutline((prev) =>
      applyOutlineDrag(prev, String(event.active.id), String(overId), placement)
    );
  };

  const addLibraryItem = (item: SectionLibraryItem) => {
    const parentId =
      selectedParent && canHoldChildren(selectedParent)
        ? selectedParent.id
        : null;
    setOutline((prev) => addFromLibrary(prev, item, parentId));
  };

  return (
    <FormModal
      open={show}
      onOpenChange={(open) => {
        if (!open) handleHide();
      }}
      title={title}
      description={
        mode === 'pick'
          ? 'Choose a template, or clone one to edit the outline before creating the draft.'
          : 'Edit the cloned or new template. Saving creates a new org template, then the draft.'
      }
      widthClassName="tw:!max-w-4xl tw:sm:!max-w-4xl"
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={mode === 'edit' ? resetEditor : handleHide}
            disabled={busy}
          >
            {mode === 'edit' ? 'Back' : 'Cancel'}
          </Button>
          <Button
            type="button"
            data-tour="reports-create-confirm"
            onClick={() => void handleConfirm()}
            disabled={busy || (mode === 'edit' && !canSubmitEdit)}
          >
            {busy
              ? 'Creating...'
              : mode === 'edit'
                ? editConfirmLabel
                : confirmLabel}
          </Button>
        </>
      }
    >
      {mode === 'pick' ? (
        <div className="tw:space-y-4" data-tour="reports-template-options">
          <RadioGroup
            value={selectedOption}
            onValueChange={setSelectedOption}
            disabled={busy}
            className="tw:gap-2"
          >
            {templates.map((template) => (
              <div
                key={template.id}
                data-tour={
                  template.id === OPORD_TEMPLATE_ID
                    ? 'reports-opord'
                    : undefined
                }
                className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:rounded-lg tw:border tw:border-border tw:px-3 tw:py-2"
              >
                <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-3">
                  <RadioGroupItem
                    value={template.id}
                    id={`template-${template.id}`}
                  />
                  <Label
                    htmlFor={`template-${template.id}`}
                    className="tw:min-w-0 tw:truncate"
                  >
                    {template.name}
                  </Label>
                  {SYSTEM_TEMPLATE_IDS.has(template.id) ? (
                    <Badge variant="outline">System</Badge>
                  ) : (
                    <Badge variant="secondary">Org</Badge>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => startClone(template)}
                >
                  Clone
                </Button>
              </div>
            ))}
          </RadioGroup>
          {canCreateReportTemplates ? (
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={startNewTemplate}
            >
              Create new template
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="tw:space-y-4">
          <div className="tw:space-y-2">
            <Label htmlFor="create-draft-template-name">Template name</Label>
            <Input
              id="create-draft-template-name"
              value={newTemplateName}
              onChange={(e) => setNewTemplateName(e.target.value)}
              placeholder="e.g. Sector daily brief"
              disabled={busy}
            />
          </div>
          <div className="tw:grid tw:gap-4 tw:md:grid-cols-2">
            <div className="tw:space-y-2">
              <div className="tw:text-sm tw:font-medium">Section library</div>
              <Input
                value={libraryQuery}
                onChange={(e) => setLibraryQuery(e.target.value)}
                placeholder="Search sections"
                disabled={busy}
              />
              <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                {selectedParent
                  ? `Adding under category “${selectedParent.title || 'Untitled category'}”.`
                  : 'Adding as an uncategorized section. Select a category in the outline to add under it.'}
              </p>
              <ScrollArea className="tw:h-72 tw:rounded-lg tw:border tw:border-border">
                <div className="tw:space-y-3 tw:p-3">
                  {libraryGroups.analytics && (
                    <LibraryGroup
                      title="Analytics"
                      items={[libraryGroups.analytics]}
                      usedIds={usedCatalogIds}
                      busy={busy}
                      onAdd={addLibraryItem}
                    />
                  )}
                  <LibraryGroup
                    title="Automated"
                    items={libraryGroups.automated}
                    usedIds={usedCatalogIds}
                    busy={busy}
                    onAdd={addLibraryItem}
                  />
                  <LibraryGroup
                    title="AI Summarized"
                    items={libraryGroups.ai}
                    usedIds={usedCatalogIds}
                    busy={busy}
                    onAdd={addLibraryItem}
                  />
                  <LibraryGroup
                    title="Manual"
                    items={libraryGroups.manual}
                    usedIds={usedCatalogIds}
                    busy={busy}
                    onAdd={addLibraryItem}
                  />
                </div>
              </ScrollArea>
            </div>
            <div className="tw:space-y-2">
              <div className="tw:text-sm tw:font-medium">Outline</div>
              <DndContext
                sensors={sensors}
                collisionDetection={outlineCollisionDetection}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={outline.map((row) => row.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="tw:space-y-2">
                    {outline.map((row, index) => (
                      <SortableOutlineRow
                        key={row.id}
                        row={row}
                        index={index}
                        busy={busy}
                        selected={selectedParentId === row.id}
                        showRemove={outline.length > 1}
                        onSelect={() =>
                          setSelectedParentId((current) =>
                            current === row.id ? null : row.id
                          )
                        }
                        onTitleChange={(title) =>
                          setOutline((prev) =>
                            updateOutlineTitle(prev, row.id, title)
                          )
                        }
                        onRemove={() => {
                          setOutline((prev) => removeOutlineRow(prev, row.id));
                          if (selectedParentId === row.id)
                            setSelectedParentId(null);
                        }}
                        onAddSection={() =>
                          setOutline((prev) => addSection(prev, row.id))
                        }
                        onChildTitleChange={(childId, title) =>
                          setOutline((prev) =>
                            updateOutlineTitle(prev, childId, title)
                          )
                        }
                        onRemoveChild={(childId) =>
                          setOutline((prev) => removeOutlineRow(prev, childId))
                        }
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
              <div className="tw:flex tw:flex-wrap tw:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOutline((prev) => addCategory(prev))}
                  disabled={busy}
                >
                  Add category
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    setOutline((prev) =>
                      addSection(prev, selectedParent?.id ?? null)
                    )
                  }
                  disabled={busy}
                >
                  Add section
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </FormModal>
  );
};

function LibraryGroup({
  title,
  items,
  usedIds,
  busy,
  onAdd,
}: {
  title: string;
  items: SectionLibraryItem[];
  usedIds: Set<string>;
  busy: boolean;
  onAdd: (item: SectionLibraryItem) => void;
}) {
  if (items.length === 0) return null;
  return (
    <div className="tw:space-y-1.5">
      <div className="tw:text-xs tw:font-medium tw:text-muted-foreground">
        {title}
      </div>
      {items.map((item) => {
        const used = usedIds.has(item.id);
        return (
          <div
            key={item.id}
            className="tw:flex tw:items-center tw:justify-between tw:gap-2"
          >
            <span className="tw:truncate tw:text-sm">{item.name}</span>
            <Button
              type="button"
              variant="outline"
              size="xs"
              disabled={busy || used}
              onClick={() => onAdd(item)}
            >
              {used ? 'Added' : 'Add'}
            </Button>
          </div>
        );
      })}
    </div>
  );
}

function SortableOutlineRow({
  row,
  index,
  busy,
  selected,
  showRemove,
  onSelect,
  onTitleChange,
  onRemove,
  onAddSection,
  onChildTitleChange,
  onRemoveChild,
}: {
  row: DraftTemplateOutlineRow;
  index: number;
  busy: boolean;
  selected: boolean;
  showRemove: boolean;
  onSelect: () => void;
  onTitleChange: (title: string) => void;
  onRemove: () => void;
  onAddSection: () => void;
  onChildTitleChange: (childId: string, title: string) => void;
  onRemoveChild: (childId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: row.id, disabled: busy });
  const isCategory = isCategoryRow(row);
  const rowLabel = isCategory ? 'category' : 'section';

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'tw:rounded-lg tw:border tw:border-border tw:p-2',
        isCategory && 'tw:bg-muted/30',
        selected && 'tw:ring-2 tw:ring-ring',
        isDragging && 'tw:opacity-60'
      )}
    >
      <div className="tw:flex tw:items-center tw:gap-2">
        <button
          type="button"
          className="tw:cursor-grab tw:text-muted-foreground tw:active:cursor-grabbing"
          aria-label={`Drag ${rowLabel} ${index + 1}`}
          disabled={busy}
          {...attributes}
          {...listeners}
        >
          <GripVertical className="tw:size-4" />
        </button>
        {isCategory ? (
          <Folder
            className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
            aria-hidden
          />
        ) : null}
        <OutlineTitle
          row={row}
          placeholder={
            isCategory ? `Category ${index + 1}` : `Section ${index + 1}`
          }
          busy={busy}
          onTitleChange={onTitleChange}
          onSelect={isCategory ? onSelect : undefined}
        />
        {showRemove && (
          <Button
            type="button"
            variant="ghost"
            size="icon-xs"
            onClick={onRemove}
            disabled={busy}
            aria-label={`Remove ${rowLabel} ${index + 1}`}
          >
            <X />
          </Button>
        )}
      </div>
      {isCategory && (
        <SortableContext
          items={row.children.map((child) => child.id)}
          strategy={verticalListSortingStrategy}
        >
          <div className="tw:mt-2 tw:space-y-2 tw:pl-6">
            {row.children.map((child, childIndex) => (
              <SortableChildRow
                key={child.id}
                child={child}
                index={childIndex}
                parentIndex={index}
                busy={busy}
                onTitleChange={(title) => onChildTitleChange(child.id, title)}
                onRemove={() => onRemoveChild(child.id)}
              />
            ))}
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={onAddSection}
              disabled={busy}
            >
              Add section
            </Button>
          </div>
        </SortableContext>
      )}
    </div>
  );
}

function SortableChildRow({
  child,
  index,
  parentIndex,
  busy,
  onTitleChange,
  onRemove,
}: {
  child: DraftTemplateOutlineRow;
  index: number;
  parentIndex: number;
  busy: boolean;
  onTitleChange: (title: string) => void;
  onRemove: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: child.id, disabled: busy });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={cn(
        'tw:flex tw:items-center tw:gap-2',
        isDragging && 'tw:opacity-60'
      )}
    >
      <button
        type="button"
        className="tw:cursor-grab tw:text-muted-foreground tw:active:cursor-grabbing"
        aria-label={`Drag section ${parentIndex + 1}.${index + 1}`}
        disabled={busy}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="tw:size-4" />
      </button>
      <OutlineTitle
        row={child}
        placeholder={`Section ${parentIndex + 1}.${index + 1}`}
        busy={busy}
        onTitleChange={onTitleChange}
      />
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        onClick={onRemove}
        disabled={busy}
        aria-label={`Remove section ${parentIndex + 1}.${index + 1}`}
      >
        <X />
      </Button>
    </div>
  );
}

function OutlineTitle({
  row,
  placeholder,
  busy,
  onTitleChange,
  onSelect,
}: {
  row: DraftTemplateOutlineRow;
  placeholder: string;
  busy: boolean;
  onTitleChange: (title: string) => void;
  onSelect?: () => void;
}) {
  if (row.sectionTemplateId) {
    return (
      <button
        type="button"
        className="tw:flex-1 tw:rounded-md tw:border tw:border-input tw:bg-muted/40 tw:px-3 tw:py-1.5 tw:text-left tw:text-sm"
        onClick={onSelect}
        disabled={busy || !onSelect}
      >
        {row.title}
        {isAnalyticsRow(row) ? (
          <span className="tw:ml-2 tw:text-xs tw:text-muted-foreground">
            Analytics
          </span>
        ) : null}
      </button>
    );
  }

  return (
    <Input
      value={row.title}
      onChange={(e) => onTitleChange(e.target.value)}
      onFocus={onSelect}
      onClick={onSelect}
      placeholder={placeholder}
      disabled={busy}
    />
  );
}

export default CreateDraftReportModal;
